import { zodResolver } from '@hookform/resolvers/zod';
import {
  DIETARY_LABELS,
  LOCALES,
  localeSchema,
  measurementSystemSchema,
  type Locale,
  type UpdateProfileRequest,
} from '@recipes/contracts';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Slider,
  Switch,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { z } from 'zod';
import { ChipsInput } from '@/components/forms/chips-input';
import { FormField } from '@/components/forms/form-field';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import { ImageUploader, type UploaderImage } from '@/components/media/image-uploader';
import { useProfile, useUpdateProfile } from '@/features/recipes/queries';
import { InstallAppCard } from '@/components/pwa/install-prompt';
import { HelpCard } from '@/routes/help-page';
import { AppearanceCard } from '@/features/appearance/appearance-card';
import { CatalogSyncSection } from '@/features/catalog/sync-section';
import { TastingPreferencesSection } from '@/features/tasting/preferences-section';
import { useGuardedLogout } from '@/features/shopping/hooks';
import { ShoppingPreferencesSection } from '@/features/shopping/preferences-section';
import { IdentityAccountLink, useAuthMode } from '@/features/auth/identity';
import { useAuth } from '@/lib/auth/auth-provider';
import { toastError } from '@/lib/errors';
import { usePublicConfig } from '@/lib/hooks/use-public-config';
import { useEnabledLocales } from '@/features/language/language-switcher';
import { applyLocale, currentFormatLocale } from '@/lib/i18n';
import { listVoices, onVoicesChanged, speak, ttsSupported } from '@/lib/tts/speak';

const profileFormSchema = z.object({
  displayName: z.string().trim().min(1, 'required').max(80),
  locale: localeSchema,
  measurementSystem: measurementSystemSchema,
  enableUnitConversion: z.boolean(),
  defaultServings: z.number().int().min(1).max(100),
  dietaryPreferences: z.array(z.string()).max(20),
  avoidedIngredients: z.array(z.string().trim().min(1).max(80)).max(100),
  speechPreferences: z.object({
    inputEnabled: z.boolean(),
    readAloudEnabled: z.boolean(),
    preferServerTranscription: z.boolean(),
    voiceName: z.string(),
    speechRate: z.number().min(0.5).max(2),
    language: z.enum(['', 'de', 'en']),
  }),
  aiConsent: z.boolean(),
});
type ProfileFormValues = z.infer<typeof profileFormSchema>;

function useVoices() {
  const [voices, setVoices] = useState(() => listVoices());
  useEffect(() => onVoicesChanged(() => setVoices(listVoices())), []);
  return voices;
}

export function ProfilePage() {
  const { t } = useTranslation();
  const authMode = useAuthMode();
  const { user, setUser } = useAuth();
  const logout = useGuardedLogout();
  const profile = useProfile();
  const aiUnlocked = Boolean(profile.data?.aiEnabled);
  const enabledLocales = useEnabledLocales();
  const update = useUpdateProfile();
  const config = usePublicConfig();
  const voices = useVoices();
  const [avatar, setAvatar] = useState<UploaderImage[]>([]);
  const [avatarChanged, setAvatarChanged] = useState(false);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      displayName: '',
      locale: 'de',
      measurementSystem: 'METRIC',
      enableUnitConversion: false,
      defaultServings: 4,
      dietaryPreferences: [],
      avoidedIngredients: [],
      speechPreferences: {
        inputEnabled: true,
        readAloudEnabled: true,
        preferServerTranscription: false,
        voiceName: '',
        speechRate: 1,
        language: '',
      },
      aiConsent: false,
    },
  });

  useEffect(() => {
    const p = profile.data;
    if (!p) return;
    form.reset({
      displayName: p.displayName,
      locale: p.locale,
      measurementSystem: p.measurementSystem,
      enableUnitConversion: p.enableUnitConversion,
      defaultServings: p.defaultServings,
      dietaryPreferences: p.dietaryPreferences,
      avoidedIngredients: p.avoidedIngredients,
      speechPreferences: {
        inputEnabled: p.speechPreferences.inputEnabled,
        readAloudEnabled: p.speechPreferences.readAloudEnabled,
        preferServerTranscription: p.speechPreferences.preferServerTranscription,
        voiceName: p.speechPreferences.voiceName ?? '',
        speechRate: p.speechPreferences.speechRate,
        language: p.speechPreferences.language ?? '',
      },
      aiConsent: p.aiEnabled && p.aiConsentAt !== null,
    });
    setAvatar(
      p.avatarUrl && p.avatarAssetId
        ? [{ assetId: p.avatarAssetId, url: p.avatarUrl, thumbnailUrl: p.avatarUrl, altText: null }]
        : [],
    );
    setAvatarChanged(false);
  }, [profile.data, form]);

  // An administrator may have unlocked (or locked) AI since this session started; the rest of
  // the app reads it from the signed-in user.
  useEffect(() => {
    const p = profile.data;
    // … or the language was changed on another device (the app shell then switches the UI).
    if (
      p &&
      user &&
      (user.aiEnabled !== p.aiEnabled ||
        user.aiConsentAt !== p.aiConsentAt ||
        user.locale !== p.locale)
    )
      setUser({ ...user, aiEnabled: p.aiEnabled, aiConsentAt: p.aiConsentAt, locale: p.locale });
  }, [profile.data, user, setUser]);

  const onSubmit = form.handleSubmit(async (values) => {
    const body: UpdateProfileRequest = {
      displayName: values.displayName,
      locale: values.locale,
      measurementSystem: values.measurementSystem,
      enableUnitConversion: values.enableUnitConversion,
      defaultServings: values.defaultServings,
      dietaryPreferences: values.dietaryPreferences as UpdateProfileRequest['dietaryPreferences'],
      avoidedIngredients: values.avoidedIngredients,
      speechPreferences: {
        ...values.speechPreferences,
        voiceName: values.speechPreferences.voiceName || null,
        language: values.speechPreferences.language || null,
      },
    };
    // While an administrator has not unlocked AI the switch is locked and nothing is sent, so
    // saving other settings never withdraws a consent given earlier.
    if (aiUnlocked) body.aiConsent = values.aiConsent;
    if (avatarChanged) body.avatarAssetId = avatar[0]?.assetId ?? null;
    try {
      const updated = await update.mutateAsync(body);
      await applyLocale(updated.locale);
      if (user)
        setUser({
          ...user,
          displayName: updated.displayName,
          avatarUrl: updated.avatarUrl,
          locale: updated.locale,
          measurementSystem: updated.measurementSystem,
          defaultServings: updated.defaultServings,
          aiEnabled: updated.aiEnabled,
          aiConsentAt: updated.aiConsentAt,
        });
      toast.success(t('profile.saved'));
    } catch (error) {
      toastError(error, t('profile.saveFailed'));
    }
  });

  const speechRate = form.watch('speechPreferences.speechRate');
  const voiceName = form.watch('speechPreferences.voiceName');
  const previewLang = form.watch('speechPreferences.language') || form.watch('locale');

  if (profile.isPending) {
    return (
      <div className="mx-auto max-w-2xl space-y-4 p-4 md:p-6" aria-busy="true">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-64" />
      </div>
    );
  }
  if (profile.isError) {
    return (
      <div className="mx-auto max-w-2xl p-6">
        <Alert variant="destructive">{t('profile.loadError')}</Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl p-4 md:p-6" data-testid="profile-page">
      <PageHeader
        title={t('profile.title')}
        description={profile.data?.email}
        actions={
          <>
            {(user?.role === 'ADMIN' || user?.role === 'SUPPORT') && (
              <Button asChild variant="outline">
                <Link
                  to={user.role === 'ADMIN' ? '/admin' : '/admin/support'}
                  data-testid="profile-admin"
                >
                  {t(user.role === 'ADMIN' ? 'admin.title' : 'admin.sections.support')}
                </Link>
              </Button>
            )}
            <Button asChild variant="outline">
              <Link to="/households" data-testid="profile-households">
                {t('households.title')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/profile/security">{t('profile.security.title')}</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/profile/data">{t('profile.data.title')}</Link>
            </Button>
            <Button variant="ghost" onClick={() => void logout()} data-testid="logout">
              {t('nav.logout')}
            </Button>
          </>
        }
      />
      {profile.data?.emailDeliveryProblem && (
        <Alert variant="warning" className="mb-6" data-testid="email-delivery-problem">
          {t('profile.emailDeliveryProblem', { email: profile.data.email })}
        </Alert>
      )}
      <form onSubmit={onSubmit} noValidate className="space-y-8">
        <section className="space-y-4" aria-labelledby="account-heading">
          <h2 id="account-heading" className="text-lg font-semibold">
            {t('profile.sections.account')}
          </h2>
          <FormField
            label={t('auth.displayName')}
            error={form.formState.errors.displayName && t('auth.errors.displayName')}
            required
          >
            <Input
              maxLength={80}
              data-testid="profile-display-name"
              readOnly={authMode.mode === 'oidc'}
              {...form.register('displayName')}
            />
          </FormField>
          {authMode.mode === 'oidc' && authMode.identity && (
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3"
              data-testid="profile-identity"
            >
              <p className="text-sm text-muted-foreground">{t('auth.identity.managedAt')}</p>
              <IdentityAccountLink identity={authMode.identity} />
            </div>
          )}
          <div>
            <Label className="mb-1 block">{t('profile.avatar')}</Label>
            <ImageUploader
              purpose="AVATAR"
              value={avatar}
              onChange={(images) => {
                setAvatar(images);
                setAvatarChanged(true);
              }}
              allowAltText={false}
              label={t('profile.avatarUpload')}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Controller
              control={form.control}
              name="locale"
              render={({ field }) => (
                <div>
                  <Label htmlFor="profile-locale" className="mb-1 block">
                    {t('profile.locale')}
                  </Label>
                  <Select
                    value={field.value}
                    onValueChange={(v) => {
                      field.onChange(v);
                      void applyLocale(v as Locale);
                    }}
                  >
                    <SelectTrigger id="profile-locale" data-testid="profile-locale">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {enabledLocales.map((code) => (
                        <SelectItem key={code} value={code} lang={code}>
                          {LOCALES[code].nativeName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">{t('language.profileHint')}</p>
                </div>
              )}
            />
            <Controller
              control={form.control}
              name="measurementSystem"
              render={({ field }) => (
                <div>
                  <Label htmlFor="profile-measurement" className="mb-1 block">
                    {t('profile.measurementSystem')}
                  </Label>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="profile-measurement">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="METRIC">{t('profile.metric')}</SelectItem>
                      <SelectItem value="US">{t('profile.us')}</SelectItem>
                      <SelectItem value="UK">{t('profile.uk')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
          </div>
          <Controller
            control={form.control}
            name="enableUnitConversion"
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch
                  id="profile-convert"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  data-testid="profile-unit-conversion"
                />
                <Label htmlFor="profile-convert">{t('profile.enableUnitConversion')}</Label>
              </div>
            )}
          />
          <FormField
            label={t('profile.defaultServings')}
            error={form.formState.errors.defaultServings && t('editor.errors.invalidServings')}
          >
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              max={100}
              className="w-32"
              data-testid="profile-default-servings"
              {...form.register('defaultServings', { valueAsNumber: true })}
            />
          </FormField>
        </section>

        <section className="space-y-4" aria-labelledby="diet-heading">
          <h2 id="diet-heading" className="text-lg font-semibold">
            {t('profile.sections.diet')}
          </h2>
          <Controller
            control={form.control}
            name="dietaryPreferences"
            render={({ field }) => (
              <fieldset>
                <legend className="mb-1 text-sm font-medium">
                  {t('profile.dietaryPreferences')}
                </legend>
                <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
                  {DIETARY_LABELS.map((label) => (
                    <div key={label} className="flex min-h-[36px] items-center gap-2">
                      <Checkbox
                        id={`diet-${label}`}
                        checked={field.value.includes(label)}
                        onCheckedChange={(v) =>
                          field.onChange(
                            v === true
                              ? [...field.value, label]
                              : field.value.filter((x) => x !== label),
                          )
                        }
                      />
                      <Label htmlFor={`diet-${label}`} className="font-normal">
                        {t(`recipes.labels.dietary.${label}`)}
                      </Label>
                    </div>
                  ))}
                </div>
              </fieldset>
            )}
          />
          <Controller
            control={form.control}
            name="avoidedIngredients"
            render={({ field }) => (
              <div>
                <Label htmlFor="profile-avoided" className="mb-1 block">
                  {t('profile.avoidedIngredients')}
                </Label>
                <ChipsInput
                  id="profile-avoided"
                  value={field.value}
                  onChange={field.onChange}
                  label={t('profile.avoidedIngredients')}
                  removeLabel={t('common.delete')}
                  placeholder={t('profile.avoidedPlaceholder')}
                  maxItems={100}
                />
              </div>
            )}
          />
        </section>

        <section className="space-y-4" aria-labelledby="speech-heading">
          <h2 id="speech-heading" className="text-lg font-semibold">
            {t('profile.sections.speech')}
          </h2>
          <Controller
            control={form.control}
            name="speechPreferences.inputEnabled"
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch id="speech-input" checked={field.value} onCheckedChange={field.onChange} />
                <Label htmlFor="speech-input">{t('profile.speech.inputEnabled')}</Label>
              </div>
            )}
          />
          <Controller
            control={form.control}
            name="speechPreferences.readAloudEnabled"
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch id="speech-read" checked={field.value} onCheckedChange={field.onChange} />
                <Label htmlFor="speech-read">{t('profile.speech.readAloudEnabled')}</Label>
              </div>
            )}
          />
          <Controller
            control={form.control}
            name="speechPreferences.preferServerTranscription"
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch id="speech-server" checked={field.value} onCheckedChange={field.onChange} />
                <Label htmlFor="speech-server">{t('profile.speech.preferServer')}</Label>
              </div>
            )}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <Controller
              control={form.control}
              name="speechPreferences.language"
              render={({ field }) => (
                <div>
                  <Label htmlFor="speech-language" className="mb-1 block">
                    {t('profile.speech.language')}
                  </Label>
                  <Select
                    value={field.value || '__auto__'}
                    onValueChange={(v) => field.onChange(v === '__auto__' ? '' : v)}
                  >
                    <SelectTrigger id="speech-language">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__auto__">{t('profile.speech.languageAuto')}</SelectItem>
                      {enabledLocales.map((code) => (
                        <SelectItem key={code} value={code} lang={code}>
                          {LOCALES[code].nativeName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <Controller
              control={form.control}
              name="speechPreferences.voiceName"
              render={({ field }) => (
                <div>
                  <Label htmlFor="speech-voice" className="mb-1 block">
                    {t('profile.speech.voice')}
                  </Label>
                  <Select
                    value={field.value || '__default__'}
                    onValueChange={(v) => field.onChange(v === '__default__' ? '' : v)}
                    disabled={!ttsSupported()}
                  >
                    <SelectTrigger id="speech-voice">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__default__">
                        {t('profile.speech.voiceDefault')}
                      </SelectItem>
                      {voices.map((v) => (
                        <SelectItem key={v.name} value={v.name}>
                          {v.name} ({v.lang})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
          </div>
          <Controller
            control={form.control}
            name="speechPreferences.speechRate"
            render={({ field }) => (
              <div>
                <Label htmlFor="speech-rate" className="mb-1 block">
                  {t('profile.speech.rate')}: {field.value.toFixed(1)}×
                </Label>
                <Slider
                  id="speech-rate"
                  min={0.5}
                  max={2}
                  step={0.1}
                  value={[field.value]}
                  onValueChange={([v]) => field.onChange(v ?? 1)}
                  aria-label={t('profile.speech.rate')}
                />
              </div>
            )}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!ttsSupported()}
            onClick={() =>
              void speak(t('profile.speech.sample'), {
                lang: previewLang,
                rate: speechRate,
                voiceName: voiceName || null,
              })
            }
          >
            {t('profile.speech.preview')}
          </Button>
        </section>

        <section className="space-y-3" aria-labelledby="ai-heading">
          <h2 id="ai-heading" className="text-lg font-semibold">
            {t('profile.sections.ai')}
          </h2>
          <Alert variant="info" title={t('profile.ai.whatIsSent')}>
            <ul className="list-disc space-y-0.5 pl-5">
              <li>{t('profile.ai.sent.text')}</li>
              <li>{t('profile.ai.sent.images')}</li>
              <li>{t('profile.ai.sent.cooking')}</li>
              <li>{t('profile.ai.sent.audio')}</li>
            </ul>
            <p className="mt-2">
              {t('profile.ai.provider', {
                provider: config.data?.ai.defaultProvider ?? '–',
                region: config.data?.ai.region ?? t('profile.ai.regionUnknown'),
              })}
            </p>
          </Alert>
          <Controller
            control={form.control}
            name="aiConsent"
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Switch
                  id="ai-consent"
                  checked={aiUnlocked && field.value}
                  onCheckedChange={field.onChange}
                  disabled={!aiUnlocked}
                  aria-describedby={aiUnlocked ? undefined : 'ai-locked-hint'}
                  data-testid="ai-consent"
                />
                <Label htmlFor="ai-consent" className={aiUnlocked ? undefined : 'opacity-60'}>
                  {t('profile.ai.consent')}
                </Label>
              </div>
            )}
          />
          {profile.data && !aiUnlocked && (
            <p
              id="ai-locked-hint"
              className="text-sm text-muted-foreground"
              data-testid="ai-locked"
            >
              {t('profile.ai.locked')}
            </p>
          )}
          {aiUnlocked && profile.data?.aiConsentAt && (
            <p className="text-xs text-muted-foreground">
              {t('profile.ai.consentedAt', {
                date: new Date(profile.data.aiConsentAt).toLocaleString(currentFormatLocale()),
              })}
            </p>
          )}
        </section>

        <div className="sticky bottom-16 flex justify-end rounded-lg border bg-card/95 p-3 shadow-lg backdrop-blur md:bottom-4">
          <Button type="submit" loading={update.isPending} data-testid="profile-save">
            {t('common.save')}
          </Button>
        </div>
      </form>
      <AppearanceCard />
      <InstallAppCard />
      <HelpCard />
      <ShoppingPreferencesSection />
      <TastingPreferencesSection />
      <CatalogSyncSection />
    </div>
  );
}
